import React, { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Loader2, X, Upload, Plus } from "lucide-react";
import { DocumentCamera, compressImage } from "@/components/DocumentCamera";
import { usePortalBase } from "@/context/PortalContext";
import { useI18n } from "@/context/I18nContext";

const IS_MOBILE = typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");

async function uploadFile(file, base) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(`${base}/files/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.stored_name;
}

function Zoomable({ src, alt = "", className, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <img src={src} alt={alt} className={`${className} cursor-zoom-in`} onClick={() => setOpen(true)} data-testid={testid} />
      {open && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid={`${testid}-lightbox`}>
          <img src={src} alt={alt} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
          <button type="button" onClick={() => setOpen(false)} className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 rounded-full p-2 text-white" data-testid={`${testid}-lightbox-close`}><X className="w-6 h-6" /></button>
        </div>
      )}
    </>
  );
}

export function ProfilePhoto({ label, value, onChange, testid, required }) {
  const base = usePortalBase();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("画像ファイルを選択してください")); return; }
    setBusy(true);
    try { onChange(await uploadFile(file, base)); toast.success(t("写真を追加しました")); }
    catch { toast.error(t("アップロードに失敗しました")); }
    finally { setBusy(false); }
  };
  const onCapture = async (file) => { setCamOpen(false); await doUpload(file); };
  return (
    <div data-testid={testid}>
      <label className="block text-sm text-gray-400 mb-1.5">{t(label)}{required && <span className="text-red-400"> *</span>}</label>
      {value ? (
        <div className="relative w-40 h-40 rounded-md overflow-hidden border border-white/10">
          <Zoomable src={`${API}${base}/files/${value}`} className="w-full h-full object-cover" testid={`${testid}-img`} />
          <button type="button" onClick={() => onChange("")} className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-black/80 rounded-full p-1" data-testid={`${testid}-remove`}><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <div className="w-full sm:w-64">
          <button type="button" onClick={() => setCamOpen(true)} className="w-full flex items-center justify-center gap-1.5 h-24 border border-dashed border-white/15 rounded-md text-gray-400 hover:bg-white/5 cursor-pointer transition-colors" data-testid={`${testid}-camera`}>
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Camera className="w-5 h-5" /><span className="text-xs">{t("撮影")}</span></>}
          </button>
        </div>
      )}
      {camOpen && <DocumentCamera face onCapture={onCapture} onClose={() => setCamOpen(false)} />}
    </div>
  );
}

const ID_TYPES = [
  { value: "residence_card", label: "在留カード", color: "#00A651" },
  { value: "drivers_license", label: "運転免許証", color: "#2563eb" },
  { value: "my_number_card", label: "マイナンバーカード", color: "#7c3aed" },
];

function IdSide({ label, value, busy, color, base, onFile, onCameraOpen, testid }) {
  const { t } = useI18n();
  return (
    <div className="flex-1">
      <div className="text-xs text-gray-400 mb-1">{t(label)}</div>
      {value ? (
        <div className="relative rounded-md overflow-hidden h-28 border" style={{ borderColor: color }}>
          <Zoomable src={`${API}${base}/files/${value}`} alt={label} className="w-full h-full object-cover" testid={`${testid}-img`} />
          <button type="button" onClick={() => onFile("")} className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full p-1 transition-colors" data-testid={`${testid}-remove`}><X className="h-3.5 w-3.5" /></button>
        </div>
      ) : (
        <div className="flex gap-1 h-28">
          {!IS_MOBILE && (
          <label className="flex-1 flex flex-col items-center justify-center gap-1 border border-dashed border-white/15 rounded-md text-gray-400 hover:bg-white/5 cursor-pointer transition-colors">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Upload className="h-4 w-4" /><span className="text-[10px]">{t("ファイル")}</span></>}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid={`${testid}-file`} />
          </label>
          )}
          <button type="button" onClick={onCameraOpen} className="flex-1 flex flex-col items-center justify-center gap-1 border border-dashed rounded-md hover:bg-white/5 transition-colors" style={{ borderColor: color }} data-testid={`${testid}-camera`}>
            <Camera className="h-5 w-5" style={{ color }} />
            <span className="text-[10px]" style={{ color }}>{t("撮影")}</span>
          </button>
        </div>
      )}
    </div>
  );
}

export function IdDocument({ docType, front, back, onDocType, onFront, onBack, testidPrefix, lockType }) {
  const base = usePortalBase();
  const { t } = useI18n();
  const [busy, setBusy] = useState("");
  const [cameraTarget, setCameraTarget] = useState(null);
  const color = ID_TYPES.find((d) => d.value === docType)?.color || "#00A651";

  const doUpload = async (side, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("画像ファイルを選択してください")); return; }
    setBusy(side);
    try {
      const compressed = await compressImage(file, docType || "residence_card");
      const name = await uploadFile(compressed, base);
      (side === "front" ? onFront : onBack)(name);
      toast.success(t("アップロードしました"));
    } catch { toast.error(t("アップロードに失敗しました")); }
    finally { setBusy(""); }
  };

  const onCapture = async (file) => {
    const side = cameraTarget;
    setCameraTarget(null);
    await doUpload(side, file);
  };

  return (
    <div data-testid={testidPrefix}>
      {!lockType && (
        <>
          <p className="text-xs text-gray-500 mb-2">{t("本人確認書類を選択し、表面・裏面を撮影またはアップロードしてください。")}</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {ID_TYPES.map((dt) => (
              <button key={dt.value} type="button" onClick={() => onDocType(dt.value)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${docType === dt.value ? "text-white border-transparent" : "bg-transparent text-gray-300 border-white/20 hover:border-white/40"}`}
                style={docType === dt.value ? { backgroundColor: dt.color } : {}}
                data-testid={`${testidPrefix}-type-${dt.value}`}>
                {t(dt.label)}
              </button>
            ))}
          </div>
        </>
      )}
      {!docType ? (
        <div className="border border-dashed border-white/15 rounded-md p-6 text-center text-sm text-gray-500">{t("書類の種類を選択してください")}</div>
      ) : (
        <div className="flex gap-3">
          <IdSide label="表面" value={front} busy={busy === "front"} color={color} base={base} onFile={(f) => (typeof f === "string" ? onFront(f) : doUpload("front", f))} onCameraOpen={() => setCameraTarget("front")} testid={`${testidPrefix}-front`} />
          <IdSide label="裏面" value={back} busy={busy === "back"} color={color} base={base} onFile={(f) => (typeof f === "string" ? onBack(f) : doUpload("back", f))} onCameraOpen={() => setCameraTarget("back")} testid={`${testidPrefix}-back`} />
        </div>
      )}
      {cameraTarget && (
        <DocumentCamera docType={docType || "residence_card"} side={cameraTarget} onCapture={onCapture} onClose={() => setCameraTarget(null)} />
      )}
    </div>
  );
}

export function PhotoMulti({ label, values = [], onChange, testid, required }) {
  const base = usePortalBase();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const names = [];
      for (const f of files) names.push(await uploadFile(f, base));
      onChange([...(values || []), ...names]);
      toast.success(t("写真を追加しました"));
    } catch { toast.error(t("アップロードに失敗しました")); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const remove = (i) => onChange((values || []).filter((_, j) => j !== i));
  return (
    <div data-testid={testid}>
      <label className="block text-sm text-gray-400 mb-1.5">{t(label)}{required && <span className="text-red-400"> *</span>}</label>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {(values || []).map((v, i) => (
          <div key={i} className="relative h-32 rounded-md overflow-hidden border border-white/10" data-testid={`${testid}-${i}`}>
            <Zoomable src={`${API}${base}/files/${v}`} className="w-full h-full object-cover" testid={`${testid}-img-${i}`} />
            <button type="button" onClick={() => remove(i)} className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-black/80 rounded-full p-1" data-testid={`${testid}-remove-${i}`}><X className="w-4 h-4" /></button>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="flex flex-col items-center justify-center h-32 border border-dashed border-white/15 rounded-md text-gray-400 hover:bg-white/5 gap-1.5 transition-colors" data-testid={`${testid}-add`}>
          {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Plus className="w-5 h-5" /><span className="text-xs">{t("写真を撮る / 追加")}</span></>}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handle} />
    </div>
  );
}
